/**
 * OpenAI SDK adapter for browser automation tool
 */

import type { ChatCompletionTool } from 'openai/resources/chat/completions.mjs';
import { executeBrowserTask } from './core.js';
import type {
  BrowserConfig,
  BrowserTaskInput,
  BrowserTaskResult,
} from './types.js';
import { BROWSER_TOOL_DESCRIPTION, BROWSER_SYSTEM_PROMPT } from './prompts.js';

/**
 * OpenAI tool definition for browser automation
 */
export const browserTool: ChatCompletionTool = {
  type: 'function',
  function: {
    name: 'browser_task',
    description: BROWSER_TOOL_DESCRIPTION,
    parameters: {
      type: 'object',
      properties: {
        task: {
          type: 'string',
          description: 'Natural language description of what to do (e.g., "Test checkout flow for buying a pineapple")',
        },
        url: {
          type: 'string',
          description: 'Starting URL (e.g., https://3000-xyz.e2b.dev). Required if navigating to a specific page.',
        },
        maxSteps: {
          type: 'number',
          description: 'Maximum number of browser actions to take (1-50). Default: 10. Use 15-30 for complex flows.',
          default: 10,
        },
        region: {
          type: 'string',
          enum: ['sfo', 'lon'],
          description: 'Browserless region: sfo (US West Coast) or lon (Europe). Default: sfo.',
          default: 'sfo',
        },
      },
      required: ['task'],
    },
  },
};

/**
 * Execute a browser task (for use in tool call handling)
 * 
 * @param input - Tool input parameters (may be JSON string)
 * @param config - Optional browser worker configuration
 * @returns Task execution result
 */
export async function execute(
  input: BrowserTaskInput | string,
  config?: BrowserConfig
): Promise<BrowserTaskResult> {
  // Parse input if it's a JSON string (from OpenAI tool call)
  const parsedInput = typeof input === 'string' ? JSON.parse(input) : input;
  return executeBrowserTask(parsedInput, config);
}

/**
 * Format browser task result for OpenAI tool result
 * 
 * Returns a concise summary suitable for agent context. The full result object
 * (with urls, errors, action_history, judgement, etc.) is available when calling
 * execute() directly, but this formatted string omits those details to save tokens.
 * 
 * @param result - Browser task result with full history data
 * @returns Formatted string summary for tool result
 */
export function formatResult(result: BrowserTaskResult): string {
  if (result.success) {
    const parts = [
      '✅ Browser task completed successfully',
      `Steps taken: ${result.stepsTaken ?? 0}`,
      result.executionTimeMs ? `Execution time: ${result.executionTimeMs}ms` : null,
      '',
      'Result:',
      result.result || 'Task completed',
    ];
    return parts.filter(Boolean).join('\n');
  }

  return `❌ Browser task failed: ${result.error || 'Unknown error'}`;
}

/**
 * Get system prompt for browser automation
 */
export function getSystemPrompt(): string {
  return BROWSER_SYSTEM_PROMPT;
}

/**
 * Create a configured browser tool with execute and formatResult methods
 * 
 * @param config - Browser worker configuration
 * @returns Tool definition with execute and formatResult methods
 * 
 * @example
 * ```typescript
 * import OpenAI from 'openai';
 * import { createBrowserTool } from 'morphsdk/tools/browser/openai';
 * 
 * const tool = createBrowserTool({
 *   apiUrl: 'https://browser-worker.example.com'
 * });
 * 
 * const client = new OpenAI();
 * 
 * const response = await client.chat.completions.create({
 *   model: 'gpt-4o',
 *   tools: [tool], // tool itself is the ChatCompletionTool
 *   messages: [{
 *     role: 'user',
 *     content: 'Test the checkout at https://3000-abc.e2b.dev'
 *   }]
 * });
 * 
 * // Execute and format
 * const result = await tool.execute(toolCallArgs);
 * const formatted = tool.formatResult(result);
 * ```
 */
export function createBrowserTool(config?: BrowserConfig) {
  return Object.assign({}, browserTool, {
    execute: async (input: BrowserTaskInput | string): Promise<BrowserTaskResult> => {
      return execute(input, config);
    },
    formatResult: (result: BrowserTaskResult): string => {
      return formatResult(result);
    },
    getSystemPrompt: (): string => {
      return getSystemPrompt();
    },
  });
}
